import { useState } from 'react';
import { ShieldOff } from 'lucide-react';
import { TabBar } from './TabBar';
import { CdsNotice } from './CdsNotice';
import { LoadDemoModal } from './LoadDemoModal';
import { useSettings } from '@/store/selectors';
import { useAppLock, LockMenu } from '@/components/AppLock';

/** PK curve logo — a stylized concentration-time profile referencing the app's domain. */
function PkLogo(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2 18 L6 18 Q7 18 7.5 10 Q8 4 9 4 Q10 4 10.5 10 Q11 18 12 18 L22 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Persistent header: app name + CDS notice + unit indicator + tab bar (PSD §8.1.1). */
export function Header(): JSX.Element {
  const settings = useSettings();
  const unitLabel = settings.unitSystem === 'si' ? 'SI (µmol/L)' : 'Conventional (mg/dL)';
  const { lock, isEnabled, enableEncryption, decision } = useAppLock();
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  return (
    <header className="app-header">
      <div className="header-row">
        <div className="header-brand">
          <span className="header-logo" aria-hidden="true">
            <PkLogo />
          </span>
          <div>
            <h1 className="header-title">Amino PK</h1>
            <span className="header-subtitle">Aminoglycoside Dosing</span>
          </div>
        </div>
        <div className="header-meta">
          {settings.showLoadDemo && (
            <button
              type="button"
              className="btn btn-sm load-demo-btn"
              onClick={() => setDemoModalOpen(true)}
              data-testid="load-demo-btn"
            >
              Load Demo
            </button>
          )}
          <span className="badge badge-info" data-testid="unit-indicator">
            {unitLabel}
          </span>
          {isEnabled && lock ? (
            <LockMenu lock={lock} />
          ) : decision !== 'pending' ? (
            <button
              type="button"
              className="lock-enable-btn"
              onClick={enableEncryption}
              title="Enable password protection"
              aria-label="Enable password protection"
            >
              <ShieldOff size={16} strokeWidth={2} />
              Enable Lock
            </button>
          ) : null}
        </div>
      </div>
      <CdsNotice />
      <TabBar />
      <LoadDemoModal open={demoModalOpen} onClose={() => setDemoModalOpen(false)} />
    </header>
  );
}
